import QrScanner from "qr-scanner";
import { useEffect, useRef, useState } from "react";
import localForage from "localforage";
import { FREYA_DEVICE_ID_KEY } from "./FreyaPrintSettings";
import { useInitCameraDevices } from "./useInitCameraDevices";
import * as Sentry from "@sentry/react";
import { CameraInitFailureReason } from "common/enums";
import i18n from "common/translations";

interface Props {
  videoRef?: HTMLVideoElement;
  onScan?: (result: QrScanner.ScanResult) => Promise<void>;
  useIndexDB?: boolean;
  deviceStorageKey?: string;
  ignoreSameAsLastQr?: boolean;
}

export type CameraInitError = {
  reason: CameraInitFailureReason;
  message: string;
  errorName: string;
  hasVideoInput: boolean;
  constraints: MediaStreamConstraints;
};

const DEFAULT_CAMERA_CONSTRAINTS: MediaStreamConstraints = { video: true, audio: false };

const getErrorName = (error: unknown) => {
  if (error instanceof Error && error.name) {
    return error.name;
  }

  if (error && typeof error === "object" && "name" in error && typeof (error as { name?: unknown }).name === "string") {
    return (error as { name: string }).name;
  }

  if (typeof error === "string") {
    return error;
  }

  return "UnknownError";
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }

  if (typeof error === "string") {
    return error;
  }

  return i18n.t("common:checkInCamera.cameraUnavailableGeneric");
};

const mapCameraInitFailureReason = (errorName: string, hasVideoInput: boolean): CameraInitFailureReason => {
  if (!hasVideoInput) {
    return CameraInitFailureReason.NO_VIDEO_INPUT;
  }

  if (["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(errorName)) {
    return CameraInitFailureReason.PERMISSION_DENIED;
  }

  if (["NotFoundError", "DevicesNotFoundError", "OverconstrainedError", "ConstraintNotSatisfiedError"].includes(errorName)) {
    return CameraInitFailureReason.NO_VIDEO_INPUT;
  }

  return CameraInitFailureReason.UNKNOWN;
};

export const useCameraScanner = ({ videoRef, onScan, useIndexDB = false, deviceStorageKey, ignoreSameAsLastQr = false }: Props) => {
  const { cameraDevices, selectedCameraDevice, qrScanner, setCamera, initCameraDevices } = useInitCameraDevices(useIndexDB);
  const lastScannedCodeRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const [cameraInitError, setCameraInitError] = useState<CameraInitError | null>(null);

  const handleScan = async (result: QrScanner.ScanResult) => {
    if (!mountedRef.current) return;
    if (result.data === lastScannedCodeRef.current && ignoreSameAsLastQr) {
      return;
    }

    lastScannedCodeRef.current = result.data;

    await onScan?.(result);
  };

  const stopScanner = () => {
    try {
      qrScanner.current.scanner?.stop();
    } catch {}
  };

  const destroyScanner = () => {
    try {
      qrScanner.current.scanner?.destroy?.();
    } catch {}
    qrScanner.current.scanner = null;
  };

  const init = async () => {
    if (!videoRef || !onScan) {
      return;
    }

    setCameraInitError(null);

    qrScanner.current.scanner = new QrScanner(videoRef, handleScan, {
      maxScansPerSecond: 0.4,
      calculateScanRegion: () => {
        return {
          x: 0,
          y: 0,
          width: undefined,
          height: undefined,
          downScaledHeight: undefined,
          downScaledWidth: undefined
        };
      }
    });

    (qrScanner.current.scanner as any).onDecodeError = () => {};
    (qrScanner.current.scanner as any).onError = () => {};

    try {
      await qrScanner.current.scanner.start();
      const device = await initCameraDevices(deviceStorageKey);
      if (deviceStorageKey && device) {
        await qrScanner.current.scanner?.setCamera(device.id);
      }
    } catch (error) {
      const availableCameras = await QrScanner.listCameras().catch(() => []);
      const hasVideoInput = availableCameras.length > 0;
      const errorName = getErrorName(error);
      const cameraInitError: CameraInitError = {
        reason: mapCameraInitFailureReason(errorName, hasVideoInput),
        message: getErrorMessage(error),
        errorName,
        hasVideoInput,
        constraints: DEFAULT_CAMERA_CONSTRAINTS
      };

      setCameraInitError(cameraInitError);

      Sentry.withScope(scope => {
        scope.setContext("camera_init", {
          errorName: cameraInitError.errorName,
          hasVideoInput: cameraInitError.hasVideoInput,
          constraints: cameraInitError.constraints,
          reason: cameraInitError.reason
        });
        Sentry.captureMessage(`Freya camera initialization failed: ${cameraInitError.reason}`, "error");
      });

      stopScanner();
      destroyScanner();
    }
  };

  const setDeviceIdToIndexDB = async (deviceId: string) => {
    await localForage.setItem(FREYA_DEVICE_ID_KEY, deviceId);
  };

  useEffect(() => {
    init();
  }, [videoRef]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopScanner();
      destroyScanner();
    };
  }, []);

  return {
    setCamera,
    cameraDevices,
    selectedCameraDevice,
    initCameraDevices,
    setDeviceIdToIndexDB,
    stopScanner,
    destroyScanner,
    cameraInitError
  };
};
